import React, { useMemo } from "react";
import { MDXProvider } from "@mdx-js/react";
import { renderNode } from "~/views/components/content/markdown";
import rehypeRaw from "rehype-raw";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";

type Props = {
  node?: any;
};

const components = {
  h2: ({ children, ...props }: any) => (
    <h2
      className="text-base sm:text-lg font-bold text-neutral-30 mt-6 mb-3 leading-tight"
      {...props}
    >
      {children}
    </h2>
  ),

  h3: ({ children, ...props }: any) => (
    <h3
      className="text-sm sm:text-base font-bold text-neutral-30 mt-4 mb-2 leading-tight"
      {...props}
    >
      {children}
    </h3>
  ),

  h4: ({ children, ...props }: any) => (
    <h4
      className="text-xs sm:text-sm font-bold text-neutral-30 mt-3 mb-2 leading-tight"
      {...props}
    >
      {children}
    </h4>
  ),

  h5: ({ children, ...props }: any) => (
    <h5
      className="text-xs font-bold text-neutral-30 mt-2 mb-1 leading-tight"
      {...props}
    >
      {children}
    </h5>
  ),

  // Paragraphs
  p: ({ children, ...props }: any) => (
    <p
      className="text-neutral-40 leading-6 font-regular text-xs sm:text-sm my-2"
      {...props}
    >
      {children}
    </p>
  ),

  // Lists
  ul: ({ children, ...props }: any) => (
    <ul className="marker:text-neutral-60 list-outside list-disc" {...props}>
      {children}
    </ul>
  ),

  ol: ({ children, ...props }: any) => (
    <ol className="list-decimal list-outside marker:text-neutral-60" {...props}>
      {children}
    </ol>
  ),

  li: ({ children, ...props }: any) => (
    <li
      className="richtext-list-item ml-4 py-2 font-medium text-xs leading-[24px] text-neutral-60"
      {...props}
    >
      {children}
    </li>
  ),

  // Blockquotes
  blockquote: ({ children, ...props }: any) => (
    <blockquote
      className="border-l-2 border-neutral-60 pl-3 my-2 italic text-neutral-60 leading-6 text-xs sm:text-sm"
      {...props}
    >
      {children}
    </blockquote>
  ),

  // Tables
  table: ({ children, ...props }: any) => (
    <div className="overflow-x-auto my-3">
      <table
        className="min-w-full border-collapse border border-neutral-20 text-xs"
        {...props}
      >
        {children}
      </table>
    </div>
  ),

  tableRow: ({ children, ...props }: any) => (
    <tr className="border-b border-neutral-20" {...props}>
      {children}
    </tr>
  ),

  tableCell: ({ children, ...props }: any) => (
    <td
      className="border border-neutral-20 px-3 py-2 text-neutral-60 text-xs"
      {...props}
    >
      {children}
    </td>
  ),

  th: ({ children, ...props }: any) => (
    <th
      className="border border-neutral-20 px-3 py-2 bg-neutral-10 font-bold text-neutral-60 text-xs text-left"
      {...props}
    >
      {children}
    </th>
  ),

  // Text formatting
  strong: ({ children, ...props }: any) => (
    <strong className="font-bold" {...props}>
      {children}
    </strong>
  ),

  em: ({ children, ...props }: any) => (
    <em className="italic font-medium" {...props}>
      {children}
    </em>
  ),

  // Links
  a: ({ children, href, ...props }: any) => (
    <a
      href={href}
      className="text-cyan hover:text-cyan-dark underline font-medium transition-colors duration-200"
      {...props}
    >
      {children}
    </a>
  ),
};

function Annotation({ node }: Props) {
  const { frontmatter, body } = node;

  const file = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .parse(body);

  const renderedContent = useMemo(() => {
    return renderNode(file, 0, components);
  }, [file]);

  return (
    <div className="my-6 sm:my-8 text-xs sm:text-sm px-4 py-1 bg-neutral-99 rounded-lg">
      <MDXProvider components={components}>{renderedContent}</MDXProvider>
    </div>
  );
}

export default Annotation;
